import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../hooks/apiClient";
import { createEngine, preloadWasmEngine, roiScreenSide, ENGINE_JS, ENGINE_WASM } from "./scanEngines";

// Качество поднимаем уже на выданном треке. Отдельным вызовом getUserMedia это делать
// нельзя: Telegram WebApp спрашивает разрешение на каждый вызов и не запоминает ответ
// даже в пределах открытого окна, поэтому перебор вариантов превращался в несколько
// запросов подряд. Разрешение и режим фокуса идут разными вызовами: часть WebView
// давится на focusMode и заодно теряет разрешение, если просить всё сразу.
const PREFERRED_RESOLUTION = {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
};

// Успех показываем ровно настолько, чтобы продавец увидел подтверждение и не решил,
// что приложение закрылось само по себе
const SUCCESS_CLOSE_DELAY = 900;

// Методы вызываем по одному: часть из них появилась только в Bot API 8.0 и на старых
// клиентах бросает WebAppMethodUnsupported. Общий try оборвал бы всю настройку окна.
function callWebAppMethod(tg, name, ...args) {
    try {
        tg[name]?.(...args);
    } catch (e) {
        console.warn(`Telegram WebApp: ${name} недоступен`, e?.message || e);
    }
}

const CAMERA_IDLE = 'idle';
const CAMERA_STARTING = 'starting';
const CAMERA_LIVE = 'live';
const CAMERA_FAILED = 'failed';

const INTRO_TITLES = {
    [CAMERA_IDLE]: 'Сканирование марки',
    [CAMERA_STARTING]: 'Включаем камеру…',
    [CAMERA_FAILED]: 'Камера недоступна',
};

const INTRO_TEXTS = {
    [CAMERA_IDLE]: 'Включите камеру и наведите рамку на марку — мелкий квадратный код сбоку пачки',
    [CAMERA_STARTING]: 'Разрешите доступ к камере, если Telegram спросит',
    [CAMERA_FAILED]: 'Проверьте разрешение на камеру и попробуйте ещё раз',
};

const PRIMARY_LABELS = {
    [CAMERA_IDLE]: 'Включить камеру',
    [CAMERA_STARTING]: 'Подключаемся…',
    [CAMERA_FAILED]: 'Попробовать снова',
};

function ScanQR() {
    const tg = useMemo(() => window.Telegram.WebApp, []);
    const videoRef = useRef(null);
    const engineRef = useRef(null);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const mountedRef = useRef(true);
    const closeTimerRef = useRef(null);

    // Пока запрос в полёте, движок успевает выдать тот же результат ещё раз
    const busyRef = useRef(false);
    // Каждый код уходит на бэкенд не больше одного раза за сеанс. Пачка остаётся
    // в кадре и после ответа, поэтому без этой защиты на успешный скан прилетал бы
    // второй запрос и в ответ 409 «код уже отсканирован» поверх начисленных баллов
    const submittedRef = useRef(new Set());

    // Режим сравнения движков для выездного замера: /scan_qr?compare=1
    const compareMode = useMemo(
        () => new URLSearchParams(window.location.search).get('compare') === '1',
        []
    );

    const [engineName, setEngineName] = useState(ENGINE_WASM);
    const [cameraState, setCameraState] = useState(CAMERA_IDLE);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [frameSize, setFrameSize] = useState(0);
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [zoomRange, setZoomRange] = useState(null);
    const [zoomedIn, setZoomedIn] = useState(false);

    // ------------------------------------------------------------------ камера

    const stopDecoding = useCallback(() => {
        if (engineRef.current) {
            engineRef.current.stop();
            engineRef.current = null;
        }
    }, []);

    const stopCamera = useCallback(() => {
        stopDecoding();

        const stream = streamRef.current;
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setTorchSupported(false);
        setTorchOn(false);
        setZoomRange(null);
        setZoomedIn(false);
    }, [stopDecoding]);

    const openCamera = useCallback(async () => {
        // Ровно один запрос разрешения. facingMode задан как ideal, поэтому вызов не падает
        // на устройствах без задней камеры — повторять его не нужно и вредно
        const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
        });

        const track = stream.getVideoTracks()[0];
        try {
            await track?.applyConstraints(PREFERRED_RESOLUTION);
        } catch (e) {
            // Работаем с тем разрешением, что дали. Доступ при этом не переспрашивается
        }

        try {
            await track?.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
        } catch (e) {
            // Без непрерывного автофокуса читается хуже, но работает
        }

        return stream;
    }, []);

    const applyTrackCapabilities = useCallback((stream) => {
        const track = stream.getVideoTracks()[0];
        if (!track?.getCapabilities) return;

        let capabilities;
        try {
            capabilities = track.getCapabilities();
        } catch (e) {
            return;
        }

        setTorchSupported(!!capabilities.torch);
        if (capabilities.zoom && capabilities.zoom.max > capabilities.zoom.min) {
            setZoomRange({ min: capabilities.zoom.min, max: capabilities.zoom.max });
        }
    }, []);

    // -------------------------------------------------------------- обработка

    const beep = useCallback(() => {
        try {
            const ctx = audioCtxRef.current;
            if (!ctx) return;

            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            oscillator.frequency.value = 950;
            gain.gain.setValueAtTime(0.12, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.16);
            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.start();
            oscillator.stop(ctx.currentTime + 0.16);
        } catch (e) {
            // звук не критичен
        }
    }, []);

    const haptic = useCallback((type) => {
        if (tg.HapticFeedback?.notificationOccurred) {
            tg.HapticFeedback.notificationOccurred(type);
        } else if (navigator.vibrate) {
            navigator.vibrate(type === 'error' ? [40, 60, 40] : 80);
        }
    }, [tg]);

    const startDecoding = useCallback(async () => {
        const stream = streamRef.current;
        const video = videoRef.current;
        if (!stream || !video || engineRef.current) return;

        const engine = createEngine(engineName);
        engineRef.current = engine;

        try {
            await engine.start({
                video,
                stream,
                onResult: (text) => {
                    if (busyRef.current || submittedRef.current.has(text)) return;
                    busyRef.current = true;
                    submittedRef.current.add(text);
                    // Останавливаем только разбор кадров: камера остаётся включённой,
                    // чтобы после ошибки можно было сразу навести на другую пачку
                    stopDecoding();
                    handleCode(text);
                },
            });

            setFrameSize(roiScreenSide(video));
        } catch (e) {
            console.error("Scan failed:", e);
            if (!mountedRef.current) return;
            setErrorMessage("Не удалось запустить сканирование. Попробуйте ещё раз");
            stopCamera();
            setCameraState(CAMERA_FAILED);
        }
        // handleCode намеренно не в зависимостях: обработчик стабилен по составу,
        // а его добавление зациклило бы пересоздание движка
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engineName, stopDecoding, stopCamera]);

    const handleCode = useCallback(async (code) => {
        console.log("Scanned text:", code);

        if (code.startsWith('https://')) {
            busyRef.current = false;
            haptic('error');
            setErrorMessage('Это рекламный QR-код. Отсканируйте марку — мелкий квадратный код сбоку пачки');
            startDecoding();
            return;
        }

        beep();
        haptic('success');

        try {
            setSending(true);
            await apiClient.post('/v1/check_code', { code });
            if (!mountedRef.current) return;

            // Камеру гасим сразу: висеть включённой ради заставки об успехе незачем
            stopCamera();
            setSending(false);
            setSuccess(true);
            closeTimerRef.current = setTimeout(() => tg.close(), SUCCESS_CLOSE_DELAY);
        } catch (e) {
            if (!mountedRef.current) return;

            // Ответа не было (сеть, таймаут) — код не засчитан, дать отсканировать
            // ту же пачку ещё раз. На ответ сервера повтор бессмыслен
            if (!e.response) submittedRef.current.delete(code);

            busyRef.current = false;
            setSending(false);
            haptic('error');
            setErrorMessage(e.response?.data?.detail || 'Ошибка обработки');
            startDecoding();
        }
    }, [tg, beep, haptic, startDecoding, stopCamera]);

    const startCamera = useCallback(async () => {
        if (!videoRef.current || streamRef.current) return;

        setErrorMessage("");
        setCameraState(CAMERA_STARTING);

        // AudioContext создаётся только внутри пользовательского жеста, а камера
        // включается кнопкой — другого жеста до первого скана может и не быть
        if (!audioCtxRef.current) {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) audioCtxRef.current = new AudioCtx();
        }

        let stream;
        try {
            stream = await openCamera();
        } catch (e) {
            console.error('Camera error:', e);
            if (!mountedRef.current) return;

            const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
            setErrorMessage(denied
                ? 'Нет доступа к камере. Разрешите доступ в настройках Telegram и попробуйте снова'
                : 'Не удалось включить камеру. Закройте другие приложения, использующие камеру, и попробуйте снова');
            setCameraState(CAMERA_FAILED);
            return;
        }

        if (!mountedRef.current) {
            stream.getTracks().forEach(track => track.stop());
            return;
        }

        streamRef.current = stream;
        applyTrackCapabilities(stream);
        setCameraState(CAMERA_LIVE);
        startDecoding();
    }, [openCamera, applyTrackCapabilities, startDecoding]);

    // ------------------------------------------------------------ инструменты

    /**
     * applyConstraints заменяет весь набор ограничений трека, а не дополняет его,
     * поэтому фонарик и зум отправляются вместе с режимом фокуса: иначе включение
     * фонарика молча выключало бы непрерывный автофокус и марка переставала читаться.
     */
    const applyCameraOptions = useCallback(async ({ torch, zoom }) => {
        const track = streamRef.current?.getVideoTracks()[0];
        if (!track) return;

        const advanced = [{ focusMode: 'continuous' }];
        if (torchSupported) advanced.push({ torch });
        if (zoomRange) advanced.push({ zoom });

        await track.applyConstraints({ advanced });
    }, [torchSupported, zoomRange]);

    const zoomValue = useCallback((zoomIn) => (
        zoomIn
            ? Math.min(zoomRange.max, Math.max(zoomRange.min, 2))
            : zoomRange.min
    ), [zoomRange]);

    const toggleTorch = useCallback(async () => {
        const next = !torchOn;
        try {
            await applyCameraOptions({ torch: next, zoom: zoomRange && zoomValue(zoomedIn) });
            setTorchOn(next);
        } catch (e) {
            console.error('Torch error:', e);
            setTorchSupported(false);
        }
    }, [torchOn, zoomedIn, zoomRange, zoomValue, applyCameraOptions]);

    const toggleZoom = useCallback(async () => {
        if (!zoomRange) return;

        const next = !zoomedIn;
        try {
            await applyCameraOptions({ torch: torchOn, zoom: zoomValue(next) });
            setZoomedIn(next);
        } catch (e) {
            console.error('Zoom error:', e);
            setZoomRange(null);
        }
    }, [zoomedIn, zoomRange, zoomValue, torchOn, applyCameraOptions]);

    const switchEngine = useCallback((name) => {
        if (name === engineName) return;
        stopDecoding();
        setEngineName(name);
    }, [engineName, stopDecoding]);

    // ---------------------------------------------------------------- эффекты

    useEffect(() => {
        mountedRef.current = true;

        // Телеграм должен отдать всю высоту и не перехватывать вертикальные свайпы:
        // на камере во весь экран потянутое вниз окно закрывало бы сканер
        callWebAppMethod(tg, 'ready');
        callWebAppMethod(tg, 'expand');
        callWebAppMethod(tg, 'requestFullscreen');
        callWebAppMethod(tg, 'disableVerticalSwipes');
        callWebAppMethod(tg, 'setHeaderColor', '#0a0b0f');
        callWebAppMethod(tg, 'setBackgroundColor', '#0a0b0f');

        // Тело страницы красим в цвет сканера: иначе на светлой теме overscroll
        // подсвечивает белую полосу по краям
        const previousBodyBackground = document.body.style.background;
        document.body.style.background = '#0a0b0f';

        preloadWasmEngine();

        return () => {
            mountedRef.current = false;
            clearTimeout(closeTimerRef.current);
            stopCamera();
            document.body.style.background = previousBodyBackground;

            callWebAppMethod(tg, 'exitFullscreen');
            callWebAppMethod(tg, 'enableVerticalSwipes');
        };
    }, [tg, stopCamera]);

    // Движок пересоздаётся после смены в режиме замера и после ошибки разбора кода,
    // камера при этом остаётся включённой
    useEffect(() => {
        if (cameraState === CAMERA_LIVE && !engineRef.current && !busyRef.current) {
            startDecoding();
        }
    }, [engineName, cameraState, startDecoding]);

    // Рамка на экране обязана совпадать с областью, которая уходит в декодер
    useEffect(() => {
        const update = () => setFrameSize(roiScreenSide(videoRef.current));

        update();
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
        };
    }, [cameraState]);

    // ------------------------------------------------------------------ вёрстка

    const frameStyle = frameSize ? { width: frameSize, height: frameSize } : undefined;

    return (
      <div className="scan">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`scan__video${cameraState === CAMERA_LIVE ? ' is-live' : ''}`}
          />

          <div className="scan__topbar">
              <span className="scan__title">Сканирование марки</span>
              <button className="scan__icon-button" onClick={() => tg.close()} aria-label="Закрыть">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
              </button>
          </div>

          {cameraState === CAMERA_LIVE && (
            <div className="scan__stage">
                <div className="scan__viewfinder" style={frameStyle}>
                    <span className="scan__corner scan__corner--tl" />
                    <span className="scan__corner scan__corner--tr" />
                    <span className="scan__corner scan__corner--bl" />
                    <span className="scan__corner scan__corner--br" />
                    <span className="scan__laser" />
                    <p className="scan__hint">
                        Наведите рамку на марку сбоку пачки, держите примерно в 15–20 см.
                        Если на пачке блик — наклоните её немного в сторону.
                    </p>
                </div>
            </div>
          )}

          {cameraState !== CAMERA_LIVE && (
            <div className="scan__stage">
                <div className="scan__intro">
                    <div className="scan__intro-icon">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />
                            <path d="M9 9h2v2H9zM13 9h2v2h-2zM9 13h2v2H9zM13 13h2v2h-2z" className="scan__intro-cells" />
                        </svg>
                    </div>
                    <h1 className="scan__intro-title">{INTRO_TITLES[cameraState]}</h1>
                    <p className="scan__intro-text">{INTRO_TEXTS[cameraState]}</p>
                </div>
            </div>
          )}

          <div className="scan__bottom">
              {cameraState === CAMERA_LIVE ? (
                <div className="scan__tools">
                    {torchSupported && (
                      <button
                        className={`scan__tool${torchOn ? ' is-active' : ''}`}
                        onClick={toggleTorch}
                        aria-pressed={torchOn}
                      >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" />
                          </svg>
                          <span>{torchOn ? 'Фонарик вкл' : 'Фонарик'}</span>
                      </button>
                    )}
                    {zoomRange && (
                      <button
                        className={`scan__tool${zoomedIn ? ' is-active' : ''}`}
                        onClick={toggleZoom}
                        aria-pressed={zoomedIn}
                      >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                              <circle cx="11" cy="11" r="6" />
                              <path d="M20 20l-4.5-4.5M8.5 11h5M11 8.5v5" />
                          </svg>
                          <span>{zoomedIn ? 'Зум ×2' : 'Зум ×1'}</span>
                      </button>
                    )}
                </div>
              ) : (
                <button
                  className="scan__primary"
                  onClick={startCamera}
                  disabled={cameraState === CAMERA_STARTING}
                >
                    {PRIMARY_LABELS[cameraState]}
                </button>
              )}

              {compareMode && (
                <div className="scan__compare">
                    <span className="scan__compare-label">Движок для замера</span>
                    <div className="scan__compare-buttons">
                        <button
                          className={`scan__chip${engineName === ENGINE_WASM ? ' is-active' : ''}`}
                          onClick={() => switchEngine(ENGINE_WASM)}
                        >
                            ZXing-C++ WASM
                        </button>
                        <button
                          className={`scan__chip${engineName === ENGINE_JS ? ' is-active' : ''}`}
                          onClick={() => switchEngine(ENGINE_JS)}
                        >
                            ZXing JS
                        </button>
                    </div>
                </div>
              )}
          </div>

          {errorMessage && (
            <div className="scan__toast" role="alert" onClick={() => setErrorMessage("")}>
                <svg viewBox="0 0 24 24" aria-hidden="true" className="scan__toast-icon">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7.5v5M12 16h.01" />
                </svg>
                <span>{errorMessage}</span>
            </div>
          )}

          {sending && (
            <div className="scan__overlay">
                <span className="scan__spinner" />
                <span className="scan__overlay-text">Проверяем код…</span>
            </div>
          )}

          {success && (
            <div className="scan__overlay scan__overlay--success">
                <span className="scan__check">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M5 12.5l4.5 4.5L19 7.5" />
                    </svg>
                </span>
                <span className="scan__overlay-text">Код принят</span>
            </div>
          )}
      </div>
    );
}

export default ScanQR;
